import axios from '@/zenky'
import storage from '@/storage'

const sessionTtlMs = 30 * 60 * 1000
const defaultFlushDelayMs = 2000
const defaultBatchSize = 20

function compactObject(obj) {
  const result = {}

  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    result[key] = value
  })

  return result
}

export default class CommerceEventsClient {
  constructor(options = {}) {
    this.queue = []
    this.flushTimer = null
    this.flushDelayMs = options.flushDelayMs || defaultFlushDelayMs
    this.batchSize = options.batchSize || defaultBatchSize
    this.memoryAnonymousId = null
    this.memorySession = null

    this.anonymousId = this.getAnonymousId()
    this.sessionId = this.getSessionId()

    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush({ all: true }).catch(() => {})
      }
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
    }
  }

  destroy() {
    this.clearFlushTimer()

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    }
  }

  track(eventName, payload = {}) {
    try {
      this.enqueue(this.buildEvent(eventName, payload))
      this.scheduleFlush()

      if (this.queue.length >= this.batchSize) {
        return this.flush()
      }
    } catch (e) {
      this.debug('Unable to enqueue commerce event.', e)
    }

    return Promise.resolve(false)
  }

  trackAndFlush(eventName, payload = {}, options = {}) {
    try {
      this.enqueue(this.buildEvent(eventName, payload))
      return this.flush({
        all: true,
        timeoutMs: options.timeoutMs,
      })
    } catch (e) {
      this.debug('Unable to enqueue commerce event for immediate flush.', e)
      return Promise.resolve(false)
    }
  }

  getIdentifiers() {
    const anonymousId = this.getAnonymousId()
    const sessionId = this.getSessionId()

    if (!anonymousId || !sessionId) {
      return null
    }

    return {
      anonymous_id: anonymousId,
      session_id: sessionId,
    }
  }

  buildEvent(eventName, payload = {}) {
    const eventId = this.generateId('evt')

    return compactObject({
      event_id: eventId,
      event_name: eventName,
      schema_version: 1,
      occurred_at: new Date().toISOString(),
      source: 'web',
      idempotency_key: `web:${eventId}`,
      anonymous_id: this.getAnonymousId(),
      session_id: this.getSessionId(),
      order_id: payload.orderId,
      context: compactObject(payload.context),
      properties: compactObject(payload.properties),
    })
  }

  enqueue(event) {
    this.queue.push(event)
  }

  scheduleFlush() {
    this.clearFlushTimer()

    this.flushTimer = setTimeout(() => {
      this.flush().catch(() => {})
    }, this.flushDelayMs)
  }

  clearFlushTimer() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
  }

  flush(options = {}) {
    this.clearFlushTimer()

    if (!this.queue.length) {
      return Promise.resolve(true)
    }

    const batchLength = options.all
      ? this.queue.length
      : Math.min(this.queue.length, this.batchSize)
    const batch = this.queue.splice(0, batchLength)

    const request = axios.post('events', { events: batch })
      .then(() => true)
      .catch(err => {
        this.queue = batch.concat(this.queue)
        this.debug('Unable to send commerce events batch.', err)

        return false
      })
      .finally(() => {
        if (this.queue.length) {
          this.scheduleFlush()
        }
      })

    if (!options.timeoutMs) {
      return request
    }

    return Promise.race([
      request,
      new Promise(resolve => setTimeout(() => resolve(false), options.timeoutMs)),
    ])
  }

  getAnonymousId() {
    if (this.anonymousId) {
      return this.anonymousId
    }

    const stored = this.getStoredValue('commerceEventsAnonymousId')

    if (stored) {
      this.anonymousId = stored
      return stored
    }

    const id = this.generateId('anon')
    this.anonymousId = id
    this.memoryAnonymousId = id
    this.setStoredValue('commerceEventsAnonymousId', id)

    return id
  }

  getSessionId() {
    const current = this.getStoredSession()

    if (current && current.id && current.expires_at > Date.now()) {
      this.touchSession(current)
      this.sessionId = current.id

      return current.id
    }

    const next = {
      id: this.generateId('sess'),
      expires_at: Date.now() + sessionTtlMs,
    }

    this.sessionId = next.id
    this.memorySession = next
    this.setStoredValue('commerceEventsSession', JSON.stringify(next))

    return next.id
  }

  touchSession(session) {
    const next = {
      id: session.id,
      expires_at: Date.now() + sessionTtlMs,
    }

    this.memorySession = next
    this.setStoredValue('commerceEventsSession', JSON.stringify(next))
  }

  getStoredSession() {
    const stored = this.getStoredValue('commerceEventsSession')

    if (!stored) {
      return this.memorySession
    }

    try {
      const parsed = JSON.parse(stored)

      if (!parsed || !parsed.id || typeof parsed.expires_at !== 'number') {
        return null
      }

      return parsed
    } catch (e) {
      return null
    }
  }

  getStoredValue(method) {
    try {
      return storage[method]()
    } catch (e) {
      return null
    }
  }

  setStoredValue(method, value) {
    try {
      storage[method](value)
    } catch (e) {}
  }

  formatPrice(price) {
    if (!price) {
      return undefined
    }

    if (price.trimmed !== undefined && price.trimmed !== null && price.trimmed.toString().length > 0) {
      return price.trimmed
    }

    if (typeof price.value === 'number') {
      return this.formatMinorPriceValue(price.value)
    }

    return undefined
  }

  formatMinorPriceValue(value) {
    const amount = Number(value) / 100

    if (!Number.isFinite(amount)) {
      return undefined
    }

    return amount
      .toFixed(2)
      .replace(/\.?0+$/, '')
  }

  getPriceMinorValue(price) {
    if (!price || typeof price.value !== 'number') {
      return null
    }

    return price.value
  }

  generateId(prefix) {
    if (
      typeof window !== 'undefined'
      && window.crypto
      && typeof window.crypto.randomUUID === 'function'
    ) {
      return `${prefix}_${window.crypto.randomUUID()}`
    }

    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`
  }

  debug(message, payload) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[CommerceEvents] ${message}`, payload)
    }
  }
}
